import {
  IBlobFilenamePair,
  WithPrimaryKey,
  type DocumentData,
  type INamedDocument,
  type IReffable,
  type WithRef,
} from '@principle-theorem/shared';
import { Observable } from 'rxjs';
import {
  CustomMappingOption,
  type ICustomMapping,
  type ICustomMappingSourceOption,
  type ICustomMappings,
} from './custom-mappings';
import { type IPracticeMigration } from './practice-migration';
import {
  ISourceLinkOption,
  type ITranslationMap,
  type ITranslationMapHandler,
} from './translation-mappings';

export interface ICustomMappingHandler<
  T extends object = DocumentData,
  R = unknown,
  AssociatedValueType = unknown,
> {
  customMapping: ICustomMapping;
  canHandle(customMapping: ICustomMapping): boolean;
  /**
   * When `true`, every destination record for this mapping must be mapped to a
   * source. The UI reads this flag to surface any unmapped destination staff.
   * Only handlers that enforce completeness (e.g. staff mapping) set it.
   */
  requireDestinationCompleteness?: true;
  upsertRecord(
    record: Omit<ITranslationMap<T, R>, 'resourceType'>,
    translationMap: ITranslationMapHandler
  ): Promise<void>;
  getRecords(
    translationMap: ITranslationMapHandler
  ): Promise<WithPrimaryKey<ITranslationMap<T, R>>[]>;
  getBySource(
    sourceId: string,
    translationMap: ITranslationMapHandler
  ): Promise<WithPrimaryKey<ITranslationMap<T, R>> | undefined>;
  getSourceOptions?(
    migration: IReffable<IPracticeMigration>
  ): Promise<ICustomMappingSourceOption[]>;
  getSelectionListOptions?(
    migration: WithRef<IPracticeMigration>
  ): Promise<CustomMappingOption[]>;
  getDestinationOptions?(
    migration: IReffable<IPracticeMigration>
  ): Promise<INamedDocument[]>;
  selectedOptionRequiresValue?(value: R): boolean;
  getAssociatedValueOptions$?(
    migration: IPracticeMigration,
    destinationValue: R
  ): Observable<AssociatedValueType[]>;
  getMappingBlob(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBlobFilenamePair>;
  downloadMapping(migration: WithRef<IPracticeMigration>): Promise<void>;
  uploadMapping(
    migration: IReffable<IPracticeMigration>,
    file: Blob
  ): Promise<void>;
  autocompleteMappings?(
    migration: IReffable<IPracticeMigration>
  ): Promise<void>;
  getFileName(): string;
  getSourceLinkOptions?(
    migration: IReffable<IPracticeMigration>
  ): Promise<ISourceLinkOption[]>;
}

export interface ICustomMappingsHandler {
  migrationType: string;
  migrationVersion: string;
  mappings: ICustomMapping[];
  mappingHandlers: ICustomMappingHandler[];
  canHandle(destination: ICustomMappings): boolean;
  addEntities(migration: WithRef<IPracticeMigration>): Promise<void>;
}
